import {
  BBBVideo,
  LookupTable,
  firstVerseNumber,
} from '@deaf-bible-society-2-0/deafbible-metadata-utils';

import getTimestamps from './api/getTimestamps';
import { getBBBVideoId } from './videoHelper';
import { hasIntroSegment, segmentIntroVerse } from './timestamp';

const verseSortValue = (video: BBBVideo, chapter: string): number => {
  const verse = firstVerseNumber(video, chapter);
  const parsed = parseInt(verse ?? '0', 10);

  return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
};

const sortChapterVideos = (videos: BBBVideo[], chapter: string): BBBVideo[] =>
  [...videos].sort((left, right) => verseSortValue(left, chapter) - verseSortValue(right, chapter));

/**
 * Finds the video that *begins* a chapter, i.e. the one that owns the chapter
 * intro (verse 0).
 *
 * A chapter can span two videos where the chapter's first numbered verse lives
 * in the previous video (e.g. Jonah: verse 2:1 is at the tail of the chapter-1
 * video, while the chapter-2 intro and 2:2-11 live in the chapter-2 video). The
 * intro therefore belongs to the earliest video whose `chapter` field equals the
 * target chapter, not necessarily the one returned by a verse-1 reference lookup.
 */
export const findChapterIntroOwner = (
  lut: LookupTable | undefined,
  book: string,
  chapter: string
): BBBVideo | undefined => {
  const chapterNumber = parseInt(chapter, 10);

  if (lut === undefined || Number.isNaN(chapterNumber)) {
    return undefined;
  }

  const owners = (lut.videosInChapter(book, chapter) ?? []).filter(
    video => video.chapter === chapterNumber
  );

  return sortChapterVideos(owners, chapter)[0];
};

/**
 * URL verse for a video's intro.
 *
 * Single-video chapters keep the chapter intro form ("0"). When a chapter has
 * multiple segments, each intro uses "{firstVerse}.0" (e.g. "1.0" and "9.0")
 * so switching between segments does not collapse onto a shared "0".
 */
export const introUrlVerseForVideo = (
  video: BBBVideo,
  chapter: string,
  chapterVideos: BBBVideo[]
): string => {
  const firstVerse = firstVerseNumber(video, chapter);

  if (firstVerse === undefined || firstVerse === '' || firstVerse === '0') {
    return '0';
  }

  if (chapterVideos.length > 1) {
    return segmentIntroVerse(firstVerse);
  }

  return '0';
};

/**
 * Returns the intro URL verse when the chapter's intro-owning video has an intro
 * segment, otherwise returns the provided fallback verse.
 */
export const resolveChapterIntroVerse = async (
  lut: LookupTable | undefined,
  code: string | undefined,
  book: string,
  chapter: string,
  fallbackVerse: string
): Promise<string> => {
  const chapterNumber = parseInt(chapter, 10);

  if (Number.isNaN(chapterNumber) || code === undefined) {
    return fallbackVerse;
  }

  const introOwner = findChapterIntroOwner(lut, book, chapter);

  if (introOwner === undefined) {
    return fallbackVerse;
  }

  try {
    const records = await getTimestamps(
      code,
      book,
      getBBBVideoId(introOwner),
      introOwner.bbb_id,
      introOwner.chapter
    );

    if (!hasIntroSegment(records, chapterNumber, introOwner.bbb_id)) {
      return fallbackVerse;
    }

    const chapterVideos = lut?.videosInChapter(book, chapter) ?? [introOwner];

    return introUrlVerseForVideo(introOwner, chapter, chapterVideos);
  } catch (error) {
    return fallbackVerse;
  }
};
